import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from "@angular/common/http";
import { Observable, of } from "rxjs";
import { switchMap, catchError, defaultIfEmpty, timeout } from "rxjs/operators";
import { CalAngularService } from "@cvx/cal-angular";
import { environment } from "../../environments/environment";

import { getClaimsFromStorage } from "../utils/storage-utils";

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(private calService: CalAngularService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Skip token injection for local assets and relative URLs
    if (req.url.startsWith('assets/') || req.url.startsWith('/assets/') || !req.url.startsWith('http')) {
      return next.handle(req);
    }

    const resource = new URL(req.url).hostname;
    const account = this.calService.getAccount();

    let claims: string | undefined;

    if (account) {
      const key = `cc.${environment.clientId}.${account.idTokenClaims?.oid}.${resource}`;
      const storedClaims = getClaimsFromStorage(key);

      if (storedClaims) {
        claims = window.atob(storedClaims);
      }
    }

    const authRequest = {
      scopes: this.getScopes(),
      account: account,
      claims: claims,
    };

    console.debug('TokenInterceptor: requesting token for', authRequest.scopes);

    return this.calService.getAADToken(authRequest.scopes, false).pipe(
      timeout(10000),
      catchError((err) => {
        console.error('TokenInterceptor: getAADToken failed or timed out', err);
        // Fallback to no token so request can proceed (some APIs may allow anonymous in dev)
        return of("");
      }),
      defaultIfEmpty(""),
      switchMap((result) => {
        let token = "";

        if (typeof result === "string") {
          token = result;
        } else if (result && typeof result === 'object' && 'accessToken' in result) {
          token = (result as any).accessToken;
        }

        // Build headers conditionally if we have a token
        let headers = req.headers
          .append("Content-Type", "application/json")
          .append("Accept", "application/json");

        if (token) {
          headers = headers.set("Authorization", `Bearer ${token}`);
        } else {
          console.warn('TokenInterceptor: proceeding without Authorization header');
        }

        const requestClone = req.clone({
          headers,
        });

        return next.handle(requestClone);
      })
    );
  }

  private getScopes(): string[] {
    return [environment.clientId + "/user_impersonation"];
  }
}
